import { useEffect } from "react";
import { Link, NavLink, useLocation } from "react-router";
import { absoluteURL, routeForPath, routes, site, type Language, type PageKey } from "../config/site";
import { common, navigation } from "../content/shared";
import { VaultMark } from "./VaultMark";
import { useRouteMetadata } from "../app/useRouteMetadata";

interface SiteShellProps {
  language: Language;
  children: React.ReactNode;
}

const navKeys: PageKey[] = ["home", "play", "support"];

export function SiteShell({ language, children }: SiteShellProps) {
  const location = useLocation();
  const route = routeForPath(location.pathname);
  const labels = navigation[language];
  const copy = common[language];
  useRouteMetadata(route);
  useEffect(() => {
    document.documentElement.lang = language;
    if (!route) document.title = language === "tr" ? "Sayfa Bulunamadı — VaultPin" : "Page Not Found — VaultPin";
  }, [language, route]);

  const homePath = language === "tr" ? "/tr/" : "/";
  const languagePath = route?.alternatePath ?? (language === "tr" ? "/" : "/tr/");

  return (
    <div className="site-shell">
      <a className="skip-link" href="#main-content">{labels.skip}</a>
      <header className="site-header">
        <div className="site-header__inner">
          <Link className="brand" to={homePath} aria-label={`${site.name} ${labels.home}`}>
            <VaultMark compact />
            <span>VaultPin</span>
          </Link>
          <nav className="main-nav" aria-label={language === "tr" ? "Ana menü" : "Main navigation"}>
            {navKeys.map((key) => {
              const item = routes.find((candidate) => candidate.language === language && candidate.key === key)!;
              return (
                <NavLink key={key} to={item.path} end={key === "home"}>
                  {labels[key]}
                </NavLink>
              );
            })}
          </nav>
          <Link className="language-link" to={languagePath} aria-label={labels.languageLabel}>
            {labels.language}
          </Link>
        </div>
      </header>

      <main id="main-content">{children}</main>

      <footer className="site-footer">
        <div className="site-footer__inner">
          <div>
            <Link className="brand brand--footer" to={homePath}>
              <VaultMark compact />
              <span>VaultPin</span>
            </Link>
            <p>{copy.footer}</p>
          </div>
          <nav className="footer-nav" aria-label={language === "tr" ? "Yasal ve destek" : "Legal and support"}>
            {(["privacy", "terms", "support"] as PageKey[]).map((key) => {
              const item = routes.find((candidate) => candidate.language === language && candidate.key === key)!;
              return <Link key={key} to={item.path}>{labels[key]}</Link>;
            })}
            <a href={`mailto:${site.email}`}>{site.email}</a>
          </nav>
          <p className="site-footer__fineprint">© 2026 {site.developer}. {copy.legalNote}</p>
        </div>
      </footer>
      <span hidden data-canonical={route ? absoluteURL(route.path) : undefined} />
    </div>
  );
}
